"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ExternalLink, Gamepad2, Maximize2 } from "lucide-react";
import AnimateOnScroll from "@/components/v3/AnimateOnScroll";
import CustomButton from "@/components/v3/CustomButton";
import { ITCH_PROFILE_URL, miniGames } from "@/constants/miniGames";

const MiniGames = () => {
  const [activeSlug, setActiveSlug] = useState(miniGames[0].slug);
  const frameWrapperRef = useRef<HTMLDivElement>(null);

  const activeGame =
    miniGames.find((game) => game.slug === activeSlug) ?? miniGames[0];

  const goFullscreen = () => {
    frameWrapperRef.current?.requestFullscreen?.();
  };

  return (
    <section
      className="bg-background"
      aria-labelledby="mini-games-heading"
      id="games"
    >
      <div className="container flex flex-col gap-8 3xl:gap-12">
        {/* Header */}
        <AnimateOnScroll className="flex flex-col gap-4">
          <span
            data-from-left
            className="flex items-center gap-2 font-crimsonText text-[20px] font-bold text-v3-highlight lg:text-[28px]"
          >
            <Gamepad2 size={24} strokeWidth={1.5} aria-hidden />
            Interactive Tools by Alexander Rodriguez
          </span>
          <h2 id="mini-games-heading" className="heading text-v3-secondary">
            Play, Reflect
            <br />
            and Learn Something
          </h2>
          <p className="max-w-[897px] font-onest text-[18px] font-normal leading-[110%] text-v3-body">
            Short browser games built around self-reflection — no download, no
            sign-up. Pick one below and play it right here on the page. They are
            for curiosity and insight, not clinical assessment, so take the
            results lightly and bring anything interesting to a session.
          </p>
        </AnimateOnScroll>

        {/* Game switcher */}
        <div
          className="flex w-full gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:grid lg:grid-cols-3 lg:overflow-visible"
          role="tablist"
          aria-label="Choose a mini game"
        >
          {miniGames.map((game) => {
            const isActive = game.slug === activeGame.slug;

            return (
              <button
                key={game.slug}
                role="tab"
                aria-selected={isActive}
                aria-controls="mini-game-frame"
                onClick={() => setActiveSlug(game.slug)}
                className={`flex w-[280px] shrink-0 items-center gap-4 rounded-[16px] border p-3 text-left transition-colors lg:w-auto ${
                  isActive
                    ? "border-v3-primary bg-v3-primary/5"
                    : "border-black/10 bg-white hover:border-v3-primary/40 hover:bg-v3-primary/[0.03]"
                }`}
              >
                <div className="relative size-[72px] shrink-0 overflow-hidden rounded-[10px] bg-black/5">
                  <Image
                    src={game.cover}
                    alt=""
                    fill
                    className="object-cover"
                    sizes="72px"
                    aria-hidden
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <h3 className="font-crimsonText text-[22px] font-semibold leading-none text-v3-secondary">
                    {game.title}
                  </h3>
                  <span className="font-onest text-[14px] font-light leading-[120%] text-v3-body">
                    {game.tagline}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active game */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-1">
              <h3 className="font-crimsonText text-[28px] font-semibold leading-none text-v3-secondary lg:text-[32px]">
                {activeGame.title} — {activeGame.tagline}
              </h3>
              <p className="max-w-[720px] font-onest text-[16px] font-light leading-[120%] text-v3-body">
                {activeGame.description}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <button
                onClick={goFullscreen}
                className="flex items-center gap-2 rounded-[8px] border border-v3-primary/30 bg-v3-primary/5 px-3 py-2 font-onest text-[15px] font-light text-v3-primary transition-colors hover:bg-v3-primary/10"
                aria-label={`Play ${activeGame.title} in fullscreen`}
              >
                <Maximize2 size={16} strokeWidth={1.5} aria-hidden />
                Fullscreen
              </button>
              <Link
                href={activeGame.itchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-[8px] border border-black/10 bg-white px-3 py-2 font-onest text-[15px] font-light text-v3-secondary transition-colors hover:bg-black/5"
              >
                <ExternalLink size={16} strokeWidth={1.5} aria-hidden />
                Open on itch.io
              </Link>
            </div>
          </div>

          <div
            ref={frameWrapperRef}
            id="mini-game-frame"
            role="tabpanel"
            aria-label={`${activeGame.title} game`}
            className="relative w-full overflow-hidden rounded-[16px] border border-black/10 bg-black"
          >
            <iframe
              // Remounts on switch so the previous game stops running.
              key={activeGame.slug}
              src={`https://itch.io/embed-upload/${activeGame.embedId}?color=021837`}
              title={`${activeGame.title} — ${activeGame.tagline}`}
              className="aspect-video h-full w-full"
              allow="autoplay; fullscreen; gamepad *; xr-spatial-tracking"
              allowFullScreen
              loading="lazy"
            />
          </div>

          <p className="font-onest text-[14px] font-light leading-[130%] text-v3-body/80">
            Games are hosted on itch.io and load inside this page. If a game
            does not appear, an ad blocker or privacy extension may be blocking
            embedded content — use{" "}
            <Link
              href={activeGame.itchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-v3-primary"
            >
              Open on itch.io
            </Link>{" "}
            instead.
          </p>
        </div>

        {/* Profile CTA */}
        <div className="flex flex-col items-start gap-4 rounded-[16px] border border-black/10 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-[640px] font-onest text-[16px] font-normal leading-[120%] text-v3-body">
            New games are added over time. See everything Alexander has
            published, including anything newer than what is listed here.
          </p>
          <CustomButton
            label="View all games on itch.io"
            href={ITCH_PROFILE_URL}
            variant="primary"
            withIcon
            size="lg"
            target="_blank"
          />
        </div>
      </div>
    </section>
  );
};

export default MiniGames;
